/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Retracked from 'js/app/retracked';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button } from '@coursera/cds-core';
import type { ClickStatus } from '@coursera/event-pulse-types';

import Alert from 'bundles/authentication-common/components/Alert';
import Form from 'bundles/authentication-common/components/Form';
import Submit from 'bundles/authentication-common/components/Submit';
import * as actions from 'bundles/authentication-common/components/actions';
import { commonStyles } from 'bundles/authentication-common/components/commonStyles';
import EmailInput from 'bundles/authentication-common/components/inputs/EmailInput';
import NameInput from 'bundles/authentication-common/components/inputs/NameInput';
import PasswordInput from 'bundles/authentication-common/components/inputs/PasswordInput';
import { LEARNER_HELP_CENTER_LINK } from 'bundles/authentication-common/constants';
import {
  ARKOSE_SIGNUP_KEY,
  RECAPTCHA_ACTIONS,
  SIGNUP_REDESIGN_RECAPTCHA_ID,
} from 'bundles/authentication-common/constants/botManager';
import {
  emailRequirementsError,
  existingEmailError,
  generalErrorMessage,
  nameRequirementsError,
  passwordRequirementsError,
  responseMessageFactory,
} from 'bundles/authentication-common/constants/errors';
import useBotManager from 'bundles/authentication-common/hooks/useBotManager';
import useTrackClickButton from 'bundles/authentication-common/hooks/useTrackClickButton';
import type { ErrorResponse, EventData, SignupSharedProps, SuccessResponse } from 'bundles/authentication-common/types';
import { getSignupFormValidationConstraints, shouldShowCheckboxTermsOfUse } from 'bundles/authentication-common/utils';
import { isVisualImprovementsToAuthModalsEnabled } from 'bundles/authentication-common/utils/experimentUtils';
import { SSO_BANNER_NAMES } from 'bundles/authentication/constants';
import SSOEnforcementBanner from 'bundles/authentication/shared/SSOEnforcementBanner';
import TermsOfUseCheckbox from 'bundles/authentication/shared/TermsOfUseCheckbox';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import AppInfoContext from 'bundles/page/contexts/AppInfoContext';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';
import { useAddProfileConsent } from 'bundles/user-consent/hooks/useAddProfileConsent';

import _t from 'i18n!nls/authentication';

type Context = {
  _eventData: EventData;
};

export enum InputFields {
  NAME = 'name',
  EMAIL = 'email',
  PASSWORD = 'password',
}

type Props = SignupSharedProps & {
  defaultEmail?: string;
  inputFieldOrdering?: InputFields[];
};

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const getStyles = (enableVisualImprovements: boolean) => ({
  form: css`
    /* This is an offset of the marginTop below to keep the space above consistent */
    margin-top: -12px;
  `,
  inputContainer: css`
    margin-top: 12px;
    display: grid;
    grid-gap: 12px;
    grid-template-columns: 1fr;
  `,
  item: css`
    :nth-child(1) {
      -ms-grid-row: 1;
    }

    :nth-child(2) {
      -ms-grid-row: 2;
    }

    :nth-child(3) {
      -ms-grid-row: 3;
    }

    :nth-child(4) {
      -ms-grid-row: 4;
    }

    :nth-child(5) {
      -ms-grid-row: 5;
    }
  `,
  reCAPTCHA: css`
    display: flex;
    justify-content: center;

    &.checkbox {
      margin-top: 8px;
    }

    :nth-of-type(n) .grecaptcha-badge {
      display: none !important;
    }
  `,
  disclaimer: css`
    margin: 0 auto !important;
  `,
  submit: css`
    margin-top: ${enableVisualImprovements ? '0px' : 'var(--cds-spacing-150)'};
  `,
  styleConflictInputFix: css`
    :nth-of-type(n) input {
      border: 0 !important;
      height: unset;
      margin-bottom: 0 !important;
      padding: 12px 16px;
      line-height: 22px;
      font-size: 16px;
      outline: none;
      box-shadow: none !important;
      color: unset;
    }

    :nth-of-type(n) input:focus:required:invalid:focus {
      color: unset;
    }

    :nth-of-type(n) input::placeholder {
      color: var(--cds-color-grey-500);
      opacity: 1;
    }

    /* Internet Explorer 10-11 */
    :nth-of-type(n) input:-ms-input-placeholder {
      color: var(--cds-color-grey-500);
      opacity: 1;
    }

    /* Edge */
    :nth-of-type(n) input:-webkit-input-placeholder {
      color: var(--cds-color-grey-500);
      opacity: 1;
    }
  `,
  termsOfUseCheckbox: css`
    svg {
      width: ${enableVisualImprovements ? '18px' : '24px'};
      height: ${enableVisualImprovements ? '18px' : '24px'};
    }
  `,
});

const SignupForm = (
  {
    navigateToLogin,
    onSuccess,
    arkoseBotManagerContext,
    verificationEmailRedirectUrl,
    enterpriseSsoProps,
    defaultEmail,
    inputFieldOrdering = [InputFields.NAME, InputFields.EMAIL, InputFields.PASSWORD],
  }: Props,
  { _eventData }: Context
) => {
  const {
    ssoError,
    isEnterpriseSsoLogin,
    isLoading,
    isRedirecting,
    onEmailChange,
    resetSsoError,
    onSubmit: enterpriseSsoOnSubmit,
  } = enterpriseSsoProps || {};
  const [message, setMessage] = useState<string | JSX.Element>('');
  const [isRunning, setIsRunning] = useState(false);
  const [emailValue, setEmailValue] = useState(defaultEmail || '');
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const touCheckboxInputRef = useRef<HTMLInputElement | null>(null);
  const showCheckboxTermsOfUse = shouldShowCheckboxTermsOfUse();
  const { trackButton } = useTrackClickButton();

  const {
    isRetrievingTokenRef,
    getTokenData: getBotManagerTokenData,
    reset: resetBotManager,
  } = useBotManager({
    arkoseBotManager: {
      context: arkoseBotManagerContext,
      publicKey: ARKOSE_SIGNUP_KEY,
    },
    reCAPTCHA: {
      container: SIGNUP_REDESIGN_RECAPTCHA_ID,
      options: { action: RECAPTCHA_ACTIONS.signup },
    },
  });

  useEffect(() => {
    if (ssoError?.response?.prefillEmail) {
      setEmailValue(ssoError.response.prefillEmail);
      onEmailChange?.(ssoError.response.prefillEmail);
    }
  }, [ssoError?.response?.prefillEmail, onEmailChange]);

  const addProfileConsent = useAddProfileConsent();
  const appInfoContext = useContext(AppInfoContext);

  useEffect(() => {
    nameInputRef.current = document.getElementById('name') as HTMLInputElement;
    emailInputRef.current = document.getElementById('email') as HTMLInputElement;
    passwordInputRef.current = document.getElementById('password') as HTMLInputElement;
    touCheckboxInputRef.current = document.getElementById('touCheckbox') as HTMLInputElement;
  }, []);

  /* tracks click events to submit button regardless of successful or not */
  const handleTracking = (status: ClickStatus) => {
    trackButton({
      name: 'signup_with_email',
      clickStatus: status,
    });
  };

  /**
   * Handles errors that occur during the signup process by resetting the reCAPTCHA,
   * setting an appropriate error message, updating the UI, and focusing the input field
   * associated with the error.
   *
   * @param {ErrorResponse} response - The error response object containing the error code and other relevant information.
   */
  const handleError = (response: ErrorResponse) => {
    handleTracking('invalid');

    resetBotManager();

    const { errorCode = '' } = response?.responseJSON || {};

    const messages = responseMessageFactory();

    if (messages[errorCode]) {
      if (errorCode === existingEmailError) {
        const errMessageForExistingAcc = messages[errorCode](navigateToLogin);
        setMessage(errMessageForExistingAcc);
        setIsRunning(false);
      } else {
        const msg = messages[errorCode as Exclude<keyof typeof messages, 'existingCourseraAccount'>];
        setMessage(msg);
        setIsRunning(false);
      }

      if (errorCode === nameRequirementsError) {
        nameInputRef.current?.focus();
      } else if (errorCode === existingEmailError || errorCode === emailRequirementsError) {
        emailInputRef.current?.focus();
      } else if (errorCode === passwordRequirementsError) {
        passwordInputRef.current?.focus();
      }
    } else {
      setMessage(
        <FormattedMessage
          message="{error} {helpCenterLink}."
          error="An unexpected error has occurred. Please try again or contact our"
          helpCenterLink={<a href={LEARNER_HELP_CENTER_LINK}>{_t('Learner Help Center')}</a>}
        />
      );
      setIsRunning(false);
    }
  };

  /**
   * Handles a successful signup by setting a success message and calling the onSuccess function.
   *
   * @param {SuccessResponse} response - The success response object containing relevant information.
   *
   * @returns {ReturnType<typeof onSuccess>} The return value of the onSuccess function.
   */
  const handleSuccess = (response: SuccessResponse): ReturnType<typeof onSuccess> => {
    handleTracking('valid');
    const successMsg = responseMessageFactory().success;

    setMessage(successMsg);

    if (showCheckboxTermsOfUse) addProfileConsent();

    return onSuccess(response);
  };

  /**
   * Handles the user signup process by validating the provided information, checking the reCAPTCHA token, and
   * calling the signup action. If the signup is successful, it handles the success response; otherwise, it handles
   * the error response.
   *
   * @param {Record<string, string>} values - An object containing the user's email, name, password.
   * @returns {Promise<void | boolean | undefined>} Returns a promise that resolves to `undefined` when the function
   * execution is complete, `false` if the email domain is invalid, or a message if the reCAPTCHA token is missing.
   */
  const handleSignup = async (values: Record<string, string>): Promise<void | boolean | undefined> => {
    // Exit early if a token retrieval is in progress to prevent multiple function calls.
    if (isRetrievingTokenRef?.current) return false;
    const { email: signupEmail, name, password } = values;

    let tokenData;
    try {
      tokenData = await getBotManagerTokenData();
    } catch {
      const msg = generalErrorMessage();
      return setMessage(msg);
    }

    setIsRunning(true);

    trackAuthAttempts(_eventData, 'signup');
    try {
      const response = await actions.signup({
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        email: signupEmail,
        name,
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        password,
        verificationEmailRedirectUrl,
        ...tokenData,
      });
      handleSuccess(response as SuccessResponse);
    } catch (error) {
      handleError(error as ErrorResponse);
    }

    return undefined;
  };

  /**
   * Resets the displayed message when an input field is marked as invalid.
   */
  const onInvalid = () => {
    handleTracking('invalid');

    setMessage('');
  };

  const handleFormSubmit = async (values: Record<string, string>): Promise<void | boolean | undefined> => {
    if (isEnterpriseSsoLogin && enterpriseSsoOnSubmit) {
      return enterpriseSsoOnSubmit();
    }
    return handleSignup(values);
  };

  const getEnterpriseSsoError = () => {
    if (isEnterpriseSsoLogin) {
      // Determine if this is a social login or password-based SSO enforcement
      const isSocialLoginError = ssoError?.code === 'enforceSsoV2';
      const bannerName = isSocialLoginError ? SSO_BANNER_NAMES.SOCIAL_LOGIN : SSO_BANNER_NAMES.PASSWORD_LOGIN;

      return (
        <SSOEnforcementBanner bannerName={bannerName}>
          {_t('This email is linked to an account that requires SSO. Please continue with SSO.')}
        </SSOEnforcementBanner>
      );
    }
    return null;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    resetSsoError?.(); // Clear the previous sso error when the email changes
    setEmailValue(email);
    onEmailChange?.(email);
  };

  const constraints = getSignupFormValidationConstraints(isEnterpriseSsoLogin || false, showCheckboxTermsOfUse);

  const styles = getStyles(isVisualImprovementsToAuthModalsEnabled());

  const NameInputComponent = () => {
    return (
      <>
        {!isEnterpriseSsoLogin && (
          <NameInput
            css={[styles.styleConflictInputFix, styles.item]}
            // Some android devices Android automatically open the keyboard on focus, blocking SSO elements.
            autoFocus={!appInfoContext?.userAgent?.isAndroid}
            ref={nameInputRef}
            autoComplete="name"
          />
        )}
      </>
    );
  };

  const EmailInputComponent = () => {
    return (
      <EmailInput
        css={[styles.styleConflictInputFix, styles.item, commonStyles.emailInputReadOnly]}
        value={emailValue}
        ref={emailInputRef}
        onChange={handleEmailChange}
        isReadOnly={!!defaultEmail}
      />
    );
  };

  const PasswordInputComponent = () => {
    return (
      <>
        {!isEnterpriseSsoLogin && (
          <PasswordInput
            css={[styles.styleConflictInputFix, styles.item]}
            supportText={_t('Between 8 and 72 characters')}
            ref={passwordInputRef}
            autoComplete="new-password"
          />
        )}
      </>
    );
  };

  const renderTextFields = () => {
    const FIELD_COMPONENTS = {
      name: NameInputComponent,
      email: EmailInputComponent,
      password: PasswordInputComponent,
    };

    return inputFieldOrdering.map((field) => FIELD_COMPONENTS[field]());
  };

  return (
    <Form
      className="rc-SignupForm"
      css={styles.form}
      constraints={constraints}
      name="signup"
      onSubmit={handleFormSubmit}
      onInvalid={onInvalid}
      hideErrors={isEnterpriseSsoLogin}
    >
      <div aria-live="assertive">
        {message && !isEnterpriseSsoLogin && (
          <Alert
            role="status"
            css={styles.item}
            type={message === responseMessageFactory().success ? 'success' : 'error'}
          >
            {message}
          </Alert>
        )}
        {getEnterpriseSsoError()}
      </div>
      <div css={styles.inputContainer}>
        {renderTextFields()}
        <div
          css={styles.reCAPTCHA}
          data-testid={SIGNUP_REDESIGN_RECAPTCHA_ID}
          id={SIGNUP_REDESIGN_RECAPTCHA_ID}
          data-size="invisible"
        />
        {showCheckboxTermsOfUse && !isEnterpriseSsoLogin && (
          <TermsOfUseCheckbox
            ref={touCheckboxInputRef}
            data-testid="signup-form-tou-checkbox"
            css={styles.termsOfUseCheckbox}
          />
        )}

        {isEnterpriseSsoLogin ? (
          <Button disabled={isLoading} loading={isRedirecting} type="submit" style={{ minWidth: '100%' }}>
            {_t('Continue with SSO')}
          </Button>
        ) : (
          <TrackedSubmit
            css={[styles.submit, styles.item]}
            data-catchpoint="signup-form-submit-button"
            data-e2e="signup-form-submit-button"
            disabled={isRunning}
            trackingName="signup_form_submit_button"
          >
            {_t('Join for Free')}
          </TrackedSubmit>
        )}
      </div>
    </Form>
  );
};

SignupForm.contextTypes = {
  _eventData: PropTypes.object.isRequired,
};

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'signup',
  },
}))(SignupForm);
